import { IonBackButton, IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonChip, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonImg, IonItem, IonLabel, IonMenuButton, IonMenuToggle, IonPage, IonProgressBar, IonRefresher, IonRefresherContent, IonRow, IonSkeletonText, IonSpinner, IonText, IonTitle, IonToolbar, RefresherEventDetail, useIonRouter, useIonViewDidEnter, useIonViewDidLeave, useIonViewWillEnter } from '@ionic/react';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { alarmOutline, alertCircleOutline, at, cardOutline, caretForwardCircleOutline, carOutline, checkboxOutline, checkmarkCircleOutline, checkmarkOutline, chevronForward, closeOutline, discSharp, funnel, funnelOutline, helpCircleOutline, helpOutline, infiniteOutline, listOutline, locateOutline, locationOutline, manOutline, menuOutline, navigateOutline, notificationsOutline, pawOutline, peopleOutline, personCircleOutline, personOutline, speedometerOutline, squareOutline, sunny, sunnyOutline, timerOutline, warningOutline, wifi } from 'ionicons/icons';
import { useCountdownTimer } from '../components/CountdownTimer';
import { useAppContext } from '../AppProvider';
import { Capacitor } from '@capacitor/core';
import { initAndroidPushRegistration } from '../utils/capPushNotifications';
import './Dashboard.css';
import { Alert, AlertTitle } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Geolocation } from '@capacitor/geolocation';
import morningBG from '../assets/images/greeting-morning.png';
import afternoonBG from '../assets/images/greeting-afternoon.png';
import eveningBG from '../assets/images/greeting-evening.png';

const Dashboard: React.FC = () => {
    const [announcements, setAnnouncements] = useState<string>('');
    const [announcementsDate, setAnnouncementsDate] = useState<string>('');
    const [alertVisible, setAlertVisible] = useState<boolean>(false);
    const [greetingBG, setGreetingBG] = useState<string>('');
    const [hasName, setHasName] = useState<boolean>(false);
    const [hasEmail, setHasEmail] = useState<boolean>(false);
    const [hasContact, setHasContact] = useState<boolean>(false);
    const [hasPlace, setHasPlace] = useState<boolean>(false);
    const [setupVisible, setSetupVisible] = useState<boolean>(true);  
    const [now, setNow] = useState<number>(Date.now());
    const [sunrise, setSunrise] = useState<string>('');
    const [sunset, setSunset] = useState<string>('');
    const [secondsLeft, setSecondsLeft] = useState<number>(0);
    
    const timerRef = useRef<number | null>(null);    
    const router = useIonRouter();
    const platform = Capacitor.getPlatform();
    
    const { networkStatus, appStatus, setAppStatus, appStatusMarkup, setAppStatusMarkup, getAppStatus, authKey, authenticateMember, authorizedMember, getAuthorizedMember, accountSetupPct, setAccountSetupPct, showLoader, setShowLoader, level, getLevel, alarms, limits, profiles, settings, getCountries, getStates, locationPerm, setLocationPerm, currentTimer, currentTimerStatus, currentTimers, pendingTimer, textLogoLoaded, fadeInTextLogo, SWFullHorizLogo, SWTextDarkLogo, showToast, present, dismiss } = useAppContext();   
    
    const { subscribe } = useCountdownTimer();
    
    //
    // Authenticate Member
    //
    useIonViewWillEnter(() => { 
        setShowLoader(true);
        const authenticate = async () => {            
            const appstat = await getAppStatus();
            if (!networkStatus && appstat === 'No') {
                router.push('/connection', 'forward');
            } else {
                const authenticated = await authenticateMember();  
                
                if(!authenticated || authenticated.mt == '') router.push('/login', 'forward');
                else {
                    await getAnnouncements();                
                    setShowLoader(false);

                    if (Capacitor.getPlatform() === 'android') {
                        initAndroidPushRegistration();
                    }
                }
            }
        };
        authenticate();
    }); 


    useEffect(() => {
        const unsub = subscribe(setSecondsLeft);
        return () => unsub();
    }, [subscribe]);

   
    useEffect(() => {
        const initGetSunData = async () => {            
            await getSunData();           
        };

        if (locationPerm) initGetSunData();        
    }, [locationPerm]);


    //
    // Clock & Calendar functions
    //
    const startClock = () => {
        // align to the next whole second to avoid drift
        const tick = () => {
            setNow(Date.now());
            const ms = 1000 - (Date.now() % 1000);
            timerRef.current = window.setTimeout(tick, ms);
        };
        // first schedule to the next boundary
        const ms = 1000 - (Date.now() % 1000);
        timerRef.current = window.setTimeout(tick, ms);
    };

    const stopClock = () => {
        if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        }
    };

    
    //
    // Handle Clock start and stop
    //
    useIonViewDidEnter(startClock);
    useIonViewDidLeave(stopClock);
    useEffect(() => stopClock, []);


    //
    // Handle change of Network Status
    //
    useEffect(() => {
        const initGetAppStatus = async () => {
            await getAppStatus();
        }
    }, [networkStatus]);    


    const timeStr = useMemo(() => {
        return new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        }).format(now);
    }, [now]);

    const dateStr = useMemo(() => {
        return new Intl.DateTimeFormat(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        }).format(now);
    }, [now]);


    //
    // Parse time periods
    //
    const parseTimePeriod = (periodSecs: number, displaySecs: boolean, round: string) => {
        const oneMinute = 60; // seconds
        const oneHour   = 60 * oneMinute;
        const oneDay    = 24 * oneHour;
        const oneWeek   = 7 * oneDay;

        let periodStr = '';
        let periodWeeksRem = 0;
        let periodDaysRem = 0;
        let periodHoursRem = 0;
        let periodMinsRem = 0;

        if(periodSecs >= oneWeek) {
            let periodWeeksDec = periodSecs / oneWeek;
            let periodWeeksInt = Math.floor(periodWeeksDec);
            periodWeeksRem = (periodWeeksDec - periodWeeksInt) * oneWeek;
            periodStr = periodWeeksInt.toString() + ' wk';
            if(periodWeeksInt >= 2) periodStr += 's';
            periodSecs = Math.ceil(periodWeeksRem);
        }
        
        if(periodSecs >= oneDay) {
            let periodDaysDec = periodSecs / oneDay;
            let periodDaysInt = Math.floor(periodDaysDec);
            periodDaysRem = (periodDaysDec - periodDaysInt) * oneDay;
            periodStr = periodStr + ' ' + periodDaysInt.toString() + ' day';
            if(periodDaysInt >= 2) periodStr += 's';
            periodSecs = Math.ceil(periodDaysRem);
        }
        
        if(periodSecs >= oneHour) {
            let periodHoursDec = periodSecs / oneHour;
            let periodHoursInt = Math.floor(periodHoursDec);
            periodHoursRem = (periodHoursDec - periodHoursInt) * oneHour;
            periodStr = periodStr + ' ' + periodHoursInt.toString() + ' hr';
            if(periodHoursInt >= 2) periodStr += 's';
            periodSecs = Math.ceil(periodHoursRem);
        }
        
        if(periodSecs >= oneMinute) {
            let periodMinutesDec = periodSecs / oneMinute;            
            let periodMinutesInt = round == 'ceil' ? Math.ceil(periodMinutesDec) : Math.floor(periodMinutesDec);
            periodMinsRem = (periodMinutesDec - periodMinutesInt) * oneMinute;
            periodStr = periodStr + ' ' + periodMinutesInt.toString() + ' min';
            if(periodMinutesInt >= 2) periodStr += 's';
            periodSecs = Math.ceil(periodMinsRem);
        } else if(periodSecs < oneMinute && periodStr == '') {
            periodStr = periodStr + ' < 1 min';
        }

        if (displaySecs) {
            if(periodSecs >= 1) {
                periodStr = periodStr + ' ' + periodSecs.toString() + ' sec';
            } else if(periodSecs == 0) {
                periodStr = periodStr + ' 00 sec'
            }
        }

        return periodStr;
    };


    //
    // Account Set-Up functions
    //
    useEffect(() => {
        if (profiles.contactsActive >= 0) {
            let completePct = 0;
            setHasName(authorizedMember.memberFirstName != '' && authorizedMember.memberLastName != '');
            setHasEmail(authorizedMember.memberEmail != '');
            setHasContact(profiles.contactsActive >= 1);
            setHasPlace(profiles.places >= 1);

            if (authorizedMember.memberFirstName != '' && authorizedMember.memberLastName != '') completePct += 25;
            if (authorizedMember.memberEmail != '') completePct += 25;
            if (profiles.contactsActive >= 1) completePct += 25;
            if (profiles.places >= 1) completePct += 25;

            setAccountSetupPct(completePct);
            setSetupVisible(completePct < 100);
        }
    }, [profiles]);

    const toggleSetupContent = () => setSetupVisible(!setupVisible);


    const getAnnouncements = async () => {
        try {
            const formData = new FormData();
            formData.append('mt', authKey.mt);
            const response = await fetch('https://api.besolowise.com/content/files/announcements.php', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if(response.ok) {
                const obj = await response.json(); 
                if(obj.success) {
                    setAnnouncementsDate(obj.updated);
                    setAnnouncements(obj.message);
                } else {
                    showToast({
                        message: 'Error: ' + obj.message,
                        duration: 2000,
                        color: 'danger',
                        position: 'bottom'
                    });
                }

            } else {
                showToast({
                    message: 'Response Error.',
                    duration: 2000,
                    color: 'danger',
                    position: 'bottom'
                });  
            }  
        } catch(e) {
            console.error(e);
            showToast({
                message: 'Connection Error. ',
                duration: 2000,
                color: 'danger',
                position: 'bottom'
            });
        }
    };

    const greeting = (t: string) => {
        const [time, modifier] = t.split(' ');
        let [hours, minutes, seconds] = time.split(':').map(Number);
        if (modifier === 'PM' && hours !== 12) hours += 12;
        if (modifier === 'AM' && hours === 12) hours = 0;

        let greeting = 'Hi';
        if (hours >= 3 && hours <= 11) greeting = 'Good morning';
        if (hours >= 12 && hours <= 16) greeting = 'Good afternoon';
        if (hours >= 17 && hours <= 23) greeting = 'Good evening';
        return greeting;
    };

    const greetingCardBG = (t: string) => {
        const [time, modifier] = t.split(' ');
        let [hours, minutes, seconds] = time.split(':').map(Number);
        if (modifier === 'PM' && hours !== 12) hours += 12;
        if (modifier === 'AM' && hours === 12) hours = 0;

        let bg = '';
        if (hours >= 3 && hours <= 11) bg = morningBG;
        if (hours >= 12 && hours <= 16) bg = afternoonBG;
        if (hours >= 17 && hours <= 23) bg = eveningBG;
        return bg;
    };


    //
    // Timer Status Chip
    //
    const statusChip = (s: string) => {
        switch(s) {
            case '0': return <IonChip className='ion-padding-end' color={'medium'}><IonIcon icon={timerOutline} className='ion-margin-end'></IonIcon> Upcoming </IonChip>
            case '1': return <IonChip className='ion-padding-end' color={'secondary'}><IonIcon icon={caretForwardCircleOutline} className='ion-margin-end'></IonIcon> Active </IonChip>
            case '2': return <IonChip className='ion-padding-end' color={'success'}><IonIcon icon={checkmarkCircleOutline} className='ion-margin-end'></IonIcon> You are Checked-In </IonChip>
            case '3': return <IonChip className='ion-padding-end' color={'warning'}><IonIcon icon={alarmOutline} className='ion-margin-end'></IonIcon> Over-Due </IonChip>
            case '4': return <IonChip className='ion-padding-end' color={'danger'}><IonIcon icon={alertCircleOutline} className='ion-margin-end'></IonIcon> ALERT </IonChip>           
            default:  return <IonChip className='ion-padding-end' color={'medium'}><IonIcon icon={helpCircleOutline} className='ion-margin-end'></IonIcon> Unknown </IonChip>
        }
    };


    //
    // Get Current Position
    //
    const getCurrentPosition = async () => {
        try {
            const CurrentPosition = await Geolocation.getCurrentPosition();
            const Coordinates = CurrentPosition.coords;
            return Coordinates;
        } catch (e) {
            return false;
        }
    };


    // Sunrise and Sunset API
    const getSunData = async () => {
        let latitude = 0;            
        let longitude = 0; 
        try {
            if (locationPerm) {
                let Coordinates = await getCurrentPosition();
                if(Coordinates !== false && Coordinates.latitude !== undefined && Coordinates.longitude !== undefined) {
                    latitude = Coordinates.latitude;
                    longitude = Coordinates.longitude;
                }           

                const response = await fetch('https://api.sunrisesunset.io/json?lat='+latitude+'&lng='+longitude, {
                    
                });
                
                const data = await response.json();
                let sr = data.results.sunrise;
                let ss = data.results.sunset;
                sr = sr.replace(/:\d{2}(?=\s)/, '');
                ss = ss.replace(/:\d{2}(?=\s)/, '');
                setSunrise(sr);
                setSunset(ss);
             }
        } catch (e) {
            console.error('Error getting Sun data:', e);
        }
    };


    const goToTimers = () => {
        router.push('/account/timers');
    }; 


    const toggleAlertContent = () => setAlertVisible(!alertVisible);
    

    const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
        setTimeout(async () => {
          await getAuthorizedMember(authKey.mt);
          await getAppStatus();
          await getAnnouncements();
          event.detail.complete();
        }, 2000);
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot='start'>
                        <IonBackButton color={'tertiary'}>Back</IonBackButton>
                    </IonButtons>
                    <IonTitle><IonImg className={textLogoLoaded ? 'fade-in' : 'fade-out'} src={settings.theme == '1' ? SWTextDarkLogo : SWFullHorizLogo} onIonImgDidLoad={fadeInTextLogo}></IonImg></IonTitle>
                    <IonButtons slot='end'>
                        <IonMenuButton color={'tertiary'} />
                    </IonButtons>                    
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding dashboard-background" id='AccountContent' fullscreen>

                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent></IonRefresherContent>
                </IonRefresher>

                {announcements != '' &&
                <div className='ion-padding'>   
                    <Alert
                        severity="info"
                        icon={<InfoOutlinedIcon fontSize="inherit" />}
                        onClick={toggleAlertContent}
                        sx={{
                        borderRadius: 2,
                        mb: 2,
                        cursor: 'pointer'
                        }}
                    >
                        <AlertTitle>Special Announcements: <span dangerouslySetInnerHTML={{ __html: announcementsDate }} /></AlertTitle>
                        {alertVisible &&
                            (<div className='ion-margin-top'>
                                <span dangerouslySetInnerHTML={{ __html: announcements }} />
                            </div>)
                        }                        
                    </Alert>
                </div>
                }

                <IonGrid>
                    <IonRow class='ion-justify-content-center'>
                        <IonCol size='12' sizeSm='12' sizeMd='12' sizeLg='12' sizeXl='12'>

                            <IonCard className='greeting' 
                                style={{
                                    'backgroundImage': 'url(' + greetingCardBG(timeStr) + ')', 
                                    'backgroundPosition': 'right 10% top 10%',
                                    'backgroundSize': 'contain',
                                    'backgroundRepeat': 'no-repeat'
                                    }}>
                                <IonCardHeader>
                                    <h2>{greeting(timeStr)}, {authorizedMember.memberFirstName}.</h2>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonGrid style={{'paddingLeft': '0', 'paddingRight': '0'}}>
                                        <IonRow>
                                            <IonCol size='12' sizeLg='7' sizeXl='8'>
                                                {authorizedMember.memberFirstName !== undefined &&
                                                    <div aria-live="polite">     
                                                        <p className="date">{dateStr}</p>
                                                        <p className='time'>{timeStr}</p>                                                        
                                                    </div>
                                                }
                                            </IonCol>
                                        </IonRow>
                                        <IonRow>
                                            {locationPerm &&
                                            <IonCol className='suntimes' size='12' sizeSm='7' sizeMd='5' sizeLg='6' sizeXl='5'>  
                                                <IonItem lines='none'>
                                                    <IonIcon aria-hidden="true" icon={sunnyOutline} className="color-gold" slot='start'></IonIcon>
                                                    <IonLabel>
                                                        {(sunrise != '' && sunset != '') ? <>{sunrise} &mdash; {sunset}</> : <>Loading...</>}
                                                        <br />
                                                        <span>Sunrise &mdash; Sunset</span>
                                                    </IonLabel>
                                                </IonItem>                                                                
                                            </IonCol>
                                            }

                                            <IonCol className='net-status' size='12' sizeSm='5' sizeMd='4' sizeLg='6' sizeXl='4'>
                                                <IonItem lines='none'>
                                                    <IonIcon aria-hidden="true" icon={wifi} slot='start'></IonIcon> 
                                                    <IonLabel>{appStatus} Network<br /><span>Connection</span></IonLabel>
                                                </IonItem>
                                            </IonCol>
                                       
                                            {/*<IonCol className='button-col' size='12' sizeMd='3' sizeLg='12' sizeXl='3'>
                                                <div aria-live="polite" className='ion-text-right' style={{'margin': 'auto 0 0'}}>
                                                    <IonButton 
                                                        fill='outline' 
                                                        routerLink={`/account/me`} 
                                                        routerDirection='forward'>
                                                            <IonIcon icon={personCircleOutline} slot='start'></IonIcon> 
                                                            View My Profile
                                                    </IonButton>
                                                </div> 
                                            </IonCol>*/}
                                        </IonRow>
                                    </IonGrid>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                    </IonRow>

                    <IonRow>
                        <IonCol size='12' sizeSm='12' sizeMd='12' sizeLg='12' sizeXl='12'> 
                            <IonCard className='ion-padding-bottom dashboard-timers'>
                                <IonCardHeader style={{'cursor': 'pointer', 'paddingBottom': '0'}} onClick={goToTimers}>
                                    <h2><IonIcon color={'secondary'} icon={alarmOutline} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Timers</IonText></h2>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonGrid>
                                        <IonRow>
                                            <IonCol className='ion-no-padding' sizeXs='12' sizeSm='6' sizeMd='4' sizeLg='5' sizeXl='5'>
                                                {currentTimers.length >= 1
                                                  ? <>  
                                                        <div>
                                                            <p className='strong'>Next Check-in:</p>
                                                            <p className='time'><strong>{currentTimer.endTime}</strong></p>
                                                            <p><em>{currentTimer.name}</em></p>   
                                                        </div>
                                                    </>
                                                  : <>  
                                                        <div>
                                                            <p className='time ion-padding-bottom'>
                                                                You have no scheduled Timers.
                                                            </p>    
                                                        </div> 
                                                    </>
                                                } 

                                            </IonCol>
                                            <IonCol className='button-col' sizeXs='12' sizeSm='6' sizeMd='8' sizeLg='7' sizeXl='7'>
                                                <div className='ion-text-right' style={{'margin': '0'}}>     
                                                    {profiles !== null && profiles.contactsActive === 0 &&
                                                        <p><IonText color={'warning'}>Still waiting for a confirmed Contact.</IonText></p>
                                                    }
                                                
                                                    {(currentTimer?.token && currentTimer?.startTime != '') && 
                                                        <IonButton 
                                                            color={'secondary'} 
                                                            routerLink={`/account/timer/view/${currentTimer.token}`} 
                                                            routerDirection='forward'>
                                                                <IonIcon icon={checkmarkOutline} slot='start'></IonIcon> Go to Next Timer
                                                        </IonButton>
                                                    }

                                                    {(currentTimer?.token && currentTimer?.startTime == '') && 
                                                        <IonButton 
                                                            color={'secondary'} 
                                                            routerLink={`/account/timer/view-daily/${currentTimer.token}`} 
                                                            routerDirection='forward'>
                                                                <IonIcon icon={checkmarkOutline} slot='start'></IonIcon> Go to Next Timer
                                                        </IonButton>
                                                    }
                                                                                                        
                                                    <IonButton 
                                                        disabled={profiles.contactsActive < 1 || alarms >= limits.timers} 
                                                        routerLink={`/account/timer/create`} 
                                                        fill='outline'                                                           
                                                        routerDirection='forward'
                                                        ><IonIcon icon={alarmOutline} slot='start'></IonIcon> Set a New Timer</IonButton>                                                
                                                    
                                                </div>
                                            </IonCol>
                                        </IonRow>
                                    </IonGrid>
                                    
                                </IonCardContent>
                            </IonCard>
                        </IonCol>                    
                    </IonRow>

                    <IonRow>
                        <IonCol size='12' sizeSm='6' sizeMd='6' sizeLg='6' sizeXl='6' className="equal-height-col"> 
                            <IonCard className='ion-margin-bottom ion-padding-bottom equal-height-card card-tier-2' routerLink='/account/alarms' routerDirection='forward'>  
                                <IonCardHeader>
                                    <h2><IonIcon color={'secondary'} icon={notificationsOutline} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Alarms</IonText></h2>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonText color={'dark'}><p className='ion-padding-start'><strong>{alarms} of {limits.timers} Alarms (past 31 days)</strong></p></IonText>
                                    <IonProgressBar buffer={1} color={alarms > limits.timers ? 'danger' : 'remove'} value={alarms/limits.timers}></IonProgressBar>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        <IonCol size='12' sizeSm='6' sizeMd='6' sizeLg='6' sizeXl='6' className="equal-height-col"> 
                            <IonCard className='ion-margin-bottom ion-padding-bottom equal-height-card card-tier-2' routerLink='/account/contacts' routerDirection='forward'>    
                                <IonCardHeader>
                                    <h2><IonIcon color={'secondary'} icon={at} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Contacts</IonText></h2>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonText color={'dark'}><p className='ion-padding-start'><strong>{profiles.contacts} of {limits.contacts} Contacts</strong></p></IonText>
                                    <IonProgressBar buffer={1} color={profiles.contacts > limits.contacts ? 'danger' : 'teal'} value={profiles.contacts/limits.contacts}></IonProgressBar>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                    </IonRow>
                    
                    <IonRow>
                        {level?.memberLevelID >= '2' &&
                          <>
                            {settings[`menuKids`] == '1' &&
                            <IonCol size='12' sizeSm='6' sizeMd='4' sizeLg='6' sizeXl='4' className="equal-height-col"> 
                                <IonCard className='ion-padding-bottom equal-height-card card-tier-3' routerLink='/account/kids' routerDirection='forward'>    
                                    <IonCardHeader>
                                        <h2><IonIcon color={'secondary'} icon={peopleOutline} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Kids</IonText></h2>
                                    </IonCardHeader>
                                    <IonCardContent>
                                        <IonText color={'dark'}><p className='ion-padding-start'><strong>{profiles.kids} of {limits.kids} Kids</strong></p></IonText>
                                        <IonProgressBar buffer={1} color={profiles.kids > limits.kids ? 'danger' : 'primary'} value={profiles.kids/limits.kids}></IonProgressBar>
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                            }

                            {settings[`menuPartners`] == '1' &&
                            <IonCol size='12' sizeSm='6' sizeMd='4' sizeLg='6' sizeXl='4' className="equal-height-col">  
                                <IonCard className='ion-padding-bottom equal-height-card card-tier-3' routerLink='/account/partners' routerDirection='forward'>   
                                    <IonCardHeader>
                                        <h2><IonIcon color={'secondary'} icon={manOutline} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Partners</IonText></h2>
                                    </IonCardHeader>
                                    <IonCardContent>
                                        <IonText color={'dark'}><p className='ion-padding-start'><strong>{profiles.partners} of Unlimited Partners</strong></p></IonText>
                                        <IonProgressBar buffer={1} color={'primary'} value={0}></IonProgressBar>
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                            }
                        
                            {settings[`menuPets`] == '1' &&
                            <IonCol size='12' sizeSm='6' sizeMd='4' sizeLg='6' sizeXl='4' className="equal-height-col"> 
                                <IonCard className='ion-padding-bottom equal-height-card card-tier-3' routerLink='/account/pets' routerDirection='forward'>            
                                    <IonCardHeader>
                                        <h2><IonIcon color={'secondary'} icon={pawOutline} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Pets</IonText></h2>
                                    </IonCardHeader>
                                    <IonCardContent>
                                        <IonText color={'dark'}><p className='ion-padding-start'><strong>{profiles.pets} of {limits.pets} Pets</strong></p></IonText>
                                        <IonProgressBar buffer={1} color={profiles.pets > limits.pets ? 'danger' : 'primary'} value={profiles.pets/limits.pets}></IonProgressBar>
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                            }
                          </>
                        }

                        <IonCol size='12' sizeSm='6' sizeMd='4' sizeLg='6' sizeXl='4' className="equal-height-col"> 
                            <IonCard className='ion-padding-bottom equal-height-card card-tier-3' routerLink='/account/places' routerDirection='forward'>    
                                <IonCardHeader>
                                    <h2><IonIcon color={'secondary'} icon={locationOutline} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Places</IonText></h2>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonText color={'dark'}><p className='ion-padding-start'><strong>{profiles.places} of {limits.places} Places</strong></p></IonText>
                                    <IonProgressBar buffer={1} color={profiles.places > limits.places ? 'danger' : 'primary'} value={profiles.places/limits.places}></IonProgressBar>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                    
                        <IonCol size='12' sizeSm='6' sizeMd='4' sizeLg='6' sizeXl='4' className="equal-height-col"> 
                            <IonCard className='ion-padding-bottom equal-height-card card-tier-3' routerLink='/account/routes' routerDirection='forward'>  
                                <IonCardHeader>
                                    <h2><IonIcon color={'secondary'} icon={navigateOutline} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Routes</IonText></h2>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonText color={'dark'}><p className='ion-padding-start'><strong>{profiles.routes} of {limits.routes} Routes</strong></p></IonText>
                                    <IonProgressBar buffer={1} color={profiles.routes > limits.routes ? 'danger' : 'primary'} value={profiles.routes/limits.routes}></IonProgressBar>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        {settings[`menuVehicles`] == '1' &&
                        <IonCol size='12' sizeSm='6' sizeMd='4' sizeLg='6' sizeXl='4' className="equal-height-col"> 
                            <IonCard className='ion-padding-bottom equal-height-card card-tier-3' routerLink='/account/vehicles' routerDirection='forward'>    
                                <IonCardHeader>
                                    <h2><IonIcon color={'secondary'} icon={carOutline} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Vehicles</IonText></h2>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonText color={'dark'}><p className='ion-padding-start'><strong>{profiles.vehicles} of {limits.vehicles} Vehicles</strong></p></IonText>
                                    <IonProgressBar buffer={1} color={profiles.vehicles > limits.vehicles ? 'danger' : 'primary'} value={profiles.vehicles/limits.vehicles}></IonProgressBar>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                        }
                    </IonRow>

                    {/*<IonRow>
                        <IonCol size='12' sizeSm='8' sizeMd='8' sizeLg='8' sizeXl='6' className="equal-height-col">                            
                            <IonCard className='ion-padding-bottom equal-height-card'>                                
                                <IonCardHeader>
                                    <h2 className='ion-text-center'><IonIcon color={'secondary'} icon={cardOutline} size='large' style={{verticalAlign: 'sub'}}></IonIcon> <IonText color={'tertiary'}>Payment Info</IonText></h2>
                                </IonCardHeader>
                                <IonCardContent>                                    
                                    <p className='ion-text-center time'>Next Payment: Not scheduled</p>
                                    <p className='ion-text-center'>Last Payment: Data not available</p>
                                    <div className='ion-text-center'>
                                        <p className='ion-text-center'><IonButton className='ion-margin-top' color={'secondary'} routerLink={`/account/preferences?payment`} routerDirection='forward'>Manage Your Payment Method</IonButton></p>                                     
                                    </div>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                    </IonRow>*/}

                </IonGrid>        
            </IonContent>
        </IonPage>
    );
};

export default Dashboard;